import { useEffect, useRef } from "react";
import { Download, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { track, trackOnce } from "@/lib/analytics";

const PRODUCT_NAME = "Ultimate Trivia Pack";

export function ContextualProductCTA({
  categorySlug,
  articleSlug,
}: {
  categorySlug?: string | null;
  articleSlug: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Track impression once it scrolls into view (per session, per article+product).
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            trackOnce(
              `cta.${articleSlug}.${PRODUCT_NAME}`,
              "Product CTA Impression",
              { product: PRODUCT_NAME, article: articleSlug, category: categorySlug ?? "" },
            );
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [articleSlug, categorySlug]);

  return (
      <div
        ref={ref}
        className="mt-7 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-5 md:p-6"
      >
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          <Download className="h-3.5 w-3.5" /> Free download
        </div>
        <h3 className="mt-2 font-display text-xl uppercase leading-tight md:text-2xl">
          The Ultimate Trivia Pack
        </h3>
        <p className="mt-2 text-sm text-muted-foreground md:text-base">
          100 questions worth asking out loud. Free, printable, and ready on your phone.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            50-page PDF · Instant download
          </span>

          <Link
            to="/shop/ultimate-trivia-pack"
            onClick={() => {
              track("Product CTA Click", {
                product: PRODUCT_NAME,
                article: articleSlug,
                category: categorySlug ?? "",
              });
            }}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110"
          >
            Get the free pack <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
  );
}
